<template>
  <v-menu
    v-model="menuAbierto"
    :close-on-content-click="false"
    offset-y
    left
    max-height="400"
    min-width="320"
    max-width="400"
    transition="slide-y-transition">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-bind="attrs"
        v-on="on"
        icon
        small
        :loading="cargando"
        :disabled="!ejercicio"
        class="ml-2"
        title="Cambiar de contrato">
        <v-icon small>mdi-swap-horizontal</v-icon>
      </v-btn>
    </template>

    <v-card class="contratos-selector-card">
      <!-- Header -->
      <v-card-title class="selector-header py-2 px-3">
        <v-icon small class="mr-2">mdi-file-sign</v-icon>
        <span class="text-subtitle-2">Contratos {{ ejercicio }}</span>
        <v-spacer></v-spacer>
        <v-chip x-small color="primary" text-color="white">
          {{ contratosFiltrados.length }}
        </v-chip>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Campo de búsqueda -->
      <div class="px-3 py-2">
        <v-text-field
          v-model="terminoBusqueda"
          placeholder="Buscar por número..."
          prepend-inner-icon="mdi-magnify"
          dense
          outlined
          hide-details
          clearable
          autofocus
          class="selector-search">
        </v-text-field>
      </div>

      <v-divider></v-divider>

      <!-- Lista de contratos -->
      <v-list
        ref="listaContratos"
        dense
        class="selector-lista py-0"
        max-height="280">
        <template v-if="contratosFiltrados.length > 0">
          <v-list-item
            v-for="contrato in contratosFiltrados"
            :key="contrato.id"
            :ref="'contrato-' + contrato.id"
            :class="{ 'contrato-activo': esContratoActual(contrato.id) }"
            @click="seleccionarContrato(contrato)">
            <v-list-item-icon class="mr-2">
              <v-icon
                small
                :color="esContratoActual(contrato.id) ? 'primary' : 'grey'">
                {{ esContratoActual(contrato.id) ? 'mdi-checkbox-marked-circle' : 'mdi-file-sign' }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title
                :class="{ 'primary--text font-weight-medium': esContratoActual(contrato.id) }">
                {{ contrato.numero }}
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action v-if="esContratoActual(contrato.id)">
              <v-chip x-small color="primary" text-color="white">
                Actual
              </v-chip>
            </v-list-item-action>
          </v-list-item>
        </template>

        <!-- Sin resultados -->
        <v-list-item v-else>
          <v-list-item-content class="text-center py-4">
            <v-icon color="grey lighten-1" size="40">mdi-file-search-outline</v-icon>
            <p class="text-caption grey--text mt-2 mb-0">
              No se encontraron contratos
            </p>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script>
import CONTRATOS_LISTADO_MINIMO from '@/graphql/querys/administracion/contratos/contratosListadoMinimo.gql';

export default {
  name: 'ContratosSelector',
  props: {
    ejercicio: {
      type: Number,
      default: null,
    },
    idContratoActual: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      menuAbierto: false,
      terminoBusqueda: '',
      contratos: [],
      cargando: false,
    };
  },
  computed: {
    /**
     * Ordena contratos por fecha de creación (más reciente primero)
     */
    contratosOrdenados() {
      if (!this.contratos || this.contratos.length === 0) return [];

      return [...this.contratos].sort((a, b) => {
        const fechaA = new Date(a.createdAt);
        const fechaB = new Date(b.createdAt);
        return fechaB - fechaA; // Más reciente primero
      });
    },

    /**
     * Filtra contratos por término de búsqueda (local, sin backend)
     */
    contratosFiltrados() {
      if (!this.contratosOrdenados || this.contratosOrdenados.length === 0) return [];

      if (!this.terminoBusqueda || this.terminoBusqueda.trim() === '') {
        return this.contratosOrdenados;
      }

      const termino = this.terminoBusqueda.toLowerCase().trim();
      return this.contratosOrdenados.filter(
        (c) => c.numero && c.numero.toLowerCase().includes(termino),
      );
    },
  },
  watch: {
    ejercicio: {
      immediate: true,
      handler(nuevoEjercicio) {
        if (nuevoEjercicio) {
          this.cargarContratos();
        }
      },
    },
    menuAbierto(val) {
      if (val) {
        // Limpiar búsqueda al abrir
        this.terminoBusqueda = '';
        // Hacer scroll al contrato actual
        this.$nextTick(() => {
          this.scrollAlContratoActual();
        });
      }
    },
  },
  methods: {
    /**
     * Cargar contratos del ejercicio
     */
    async cargarContratos() {
      if (!this.ejercicio) return;

      this.cargando = true;

      try {
        const { data } = await this.$apollo.query({
          query: CONTRATOS_LISTADO_MINIMO,
          variables: {
            ejercicio: this.ejercicio,
          },
          fetchPolicy: 'cache-first',
        });

        if (data && data.contratosByEjercicio) {
          this.contratos = data.contratosByEjercicio;
        }
      } catch (error) {
        console.error('Error al cargar contratos:', error);
      } finally {
        this.cargando = false;
      }
    },

    /**
     * Verificar si es el contrato actual
     */
    esContratoActual(id) {
      return this.idContratoActual === id;
    },

    /**
     * Hacer scroll al contrato actualmente seleccionado
     */
    scrollAlContratoActual() {
      if (!this.idContratoActual) return;

      const refName = `contrato-${this.idContratoActual}`;
      const itemRef = this.$refs[refName];

      if (itemRef && itemRef[0] && itemRef[0].$el) {
        this.$nextTick(() => {
          itemRef[0].$el.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        });
      }
    },

    /**
     * Seleccionar un contrato y navegar
     */
    seleccionarContrato(contrato) {
      if (this.esContratoActual(contrato.id)) {
        // Ya está en este contrato, solo cerrar
        this.menuAbierto = false;
        return;
      }

      this.menuAbierto = false;

      // Navegar al contrato seleccionado
      this.$router.push({
        name: 'contrato',
        params: { idContrato: contrato.id },
      }).catch(() => {
        // Ignorar error de navegación duplicada
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.contratos-selector-card {
  border-radius: 8px !important;
  overflow: hidden;

  .selector-header {
    background-color: #f5f5f5;
  }

  .selector-search {
    ::v-deep .v-input__slot {
      min-height: 36px !important;
    }
  }

  .selector-lista {
    overflow-y: auto;

    .v-list-item {
      min-height: 40px;
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: rgba(0, 0, 0, 0.04);
      }

      &.contrato-activo {
        background-color: rgba(25, 118, 210, 0.08);
      }
    }
  }
}
</style>
